import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { EC } from "../../utils/const/ecommerce-colors-const";

export default function ProductImageGallery({ images = [], alt = "" }) {
    const { t } = useTranslation();
    const galleryImages = images.map((img) => ({
        ...img,
        label: img.labelKey ? t(img.labelKey) : img.label,
    }));
    const [activeIndex, setActiveIndex] = useState(0);

    const goTo = useCallback(
        (index) => {
            if (!galleryImages.length) return;
            const next = (index + galleryImages.length) % galleryImages.length;
            setActiveIndex(next);
        },
        [galleryImages.length]
    );

    if (!galleryImages.length) {
        return (
            <div className="flex aspect-square items-center justify-center rounded-3xl border border-slate-200 bg-slate-100 text-slate-400">
                {t("ct_no_images")}
            </div>
        );
    }

    const activeImage = galleryImages[activeIndex];

    return (
        <div className="flex flex-col gap-3 sm:gap-4">
            <div className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg">
                <div className="relative aspect-square w-full sm:aspect-[4/3] lg:aspect-square">
                    <img
                        key={activeImage.src}
                        src={activeImage.src}
                        alt={activeImage.label ? `${alt} — ${activeImage.label}` : alt}
                        className="h-full w-full object-cover transition-opacity duration-300"
                    />

                    {galleryImages.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={() => goTo(activeIndex - 1)}
                                className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow-md opacity-0 transition hover:bg-white group-hover:opacity-100 focus:opacity-100 sm:left-4 sm:h-11 sm:w-11"
                                aria-label={t("ct_gallery_prev")}
                            >
                                ‹
                            </button>
                            <button
                                type="button"
                                onClick={() => goTo(activeIndex + 1)}
                                className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow-md opacity-0 transition hover:bg-white group-hover:opacity-100 focus:opacity-100 sm:right-4 sm:h-11 sm:w-11"
                                aria-label={t("ct_gallery_next")}
                            >
                                ›
                            </button>
                            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5 sm:bottom-4">
                                {galleryImages.map((img, index) => (
                                    <button
                                        key={img.src}
                                        type="button"
                                        onClick={() => setActiveIndex(index)}
                                        className={`rounded-full transition-all ${
                                            index === activeIndex ? EC.gallery.dotActive : EC.gallery.dotInactive
                                        }`}
                                        aria-label={`${t("ct_gallery_go_to")} ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {activeImage.label && (
                    <p className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-center text-xs font-medium text-slate-500 sm:text-sm">
                        {activeImage.label}
                    </p>
                )}
            </div>

            {galleryImages.length > 1 && (
                <div
                    className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-3"
                    role="tablist"
                    aria-label={t("ct_gallery_thumbnails")}
                >
                    {galleryImages.map((img, index) => (
                        <button
                            key={img.src}
                            type="button"
                            role="tab"
                            aria-selected={index === activeIndex}
                            onClick={() => setActiveIndex(index)}
                            className={`relative shrink-0 overflow-hidden rounded-xl border-2 transition ${
                                index === activeIndex ? EC.gallery.thumbActive : EC.gallery.thumbInactive
                            }`}
                            style={{ width: "4.5rem", height: "4.5rem" }}
                        >
                            <img
                                src={img.src}
                                alt={img.label || `${alt} ${index + 1}`}
                                className="h-full w-full object-cover"
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
